import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { ShieldCheck } from 'lucide-react';
import { C, FONTS } from '../../tokens/design';
import { useApiClient } from '../../api/apiClient';
import { authService } from '../../services/authService';

export default function MfaSetup() {
  const navigate = useNavigate();
  const api = useApiClient();

  const [secret, setSecret] = useState(null);
  const [qrUri, setQrUri] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('loading'); // loading | scan | verify | done

  useEffect(() => {
    api.post('/auth/enroll-mfa')
      .then(data => {
        setSecret(data.secret);
        setQrUri(data.otpUri || data.otpauthUrl || data.qrUri);
        setStep('scan');
      })
      .catch(err => {
        setError(err.message);
        setStep('scan');
      });
  }, []);

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await api.post('/auth/confirm-mfa', { code: totpCode });
      // Backend re-issues tokens with mfa_verified=true after enrollment
      if (data?.accessToken) {
        authService.storeTokens(data.accessToken, data.refreshToken, data.expiresIn);
      }
      setStep('done');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8,
    padding: '12px 14px', color: C.t1, fontSize: 13, fontFamily: FONTS.mono, outline: 'none',
    boxSizing: 'border-box',
  };

  const btnGradient = `linear-gradient(135deg, #e94560, #1a1a2e)`;

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.bg }}>
      <div style={{ width: 420, background: C.s1, border: `1px solid ${C.b1}`, borderRadius: 12, padding: 32 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: C.t1, fontFamily: FONTS.sans, marginBottom: 6 }}>Set Up Two-Factor Authentication</div>
        <div style={{ fontSize: 12, color: C.t2, fontFamily: FONTS.mono, marginBottom: 24 }}>Scan the QR code with your authenticator app</div>

        {error && (
          <div style={{ padding: '10px 14px', borderRadius: 8, background: C.redBg, border: `1px solid ${C.red}33`, marginBottom: 16, fontSize: 11, color: C.red, fontFamily: FONTS.mono }}>{error}</div>
        )}

        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: 40, fontSize: 11, color: C.t2, fontFamily: FONTS.mono }}>Generating secret...</div>
        )}

        {step === 'scan' && (
          <>
            {qrUri && (
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20, padding: 16, background: '#fff', borderRadius: 8 }}>
                <QRCodeSVG value={qrUri} size={200} />
              </div>
            )}

            {secret && (
              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 10, color: C.t3, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>Manual Entry Key</div>
                <div style={{ padding: '10px 14px', background: C.s2, border: `1px solid ${C.b1}`, borderRadius: 8, fontSize: 13, fontFamily: FONTS.mono, color: C.t1, wordBreak: 'break-all', userSelect: 'all' }}>{secret}</div>
              </div>
            )}

            <form onSubmit={handleVerify}>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 10, color: C.t2, fontFamily: FONTS.mono, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>Verification Code</label>
                <input
                  value={totpCode} onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" type="text" autoFocus autoComplete="one-time-code"
                  inputMode="numeric" maxLength={6} required
                  style={{ ...inputStyle, textAlign: 'center', fontSize: 24, fontWeight: 700, letterSpacing: '0.3em' }}
                />
              </div>

              <button type="submit" disabled={loading || totpCode.length !== 6} style={{
                width: '100%', padding: '13px 0', borderRadius: 8, border: 'none', cursor: loading ? 'wait' : 'pointer',
                background: loading || totpCode.length !== 6 ? C.t3 : btnGradient,
                color: '#fff', fontSize: 14, fontWeight: 700, fontFamily: FONTS.sans,
              }}>{loading ? 'Verifying...' : 'Verify & Enable MFA'}</button>
            </form>
          </>
        )}

        {step === 'done' && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, color: C.grn }}><ShieldCheck size={36} strokeWidth={1.5} /></div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.grn, fontFamily: FONTS.sans, marginBottom: 8 }}>MFA Enabled Successfully</div>
            <div style={{ fontSize: 11, color: C.t2, fontFamily: FONTS.mono, marginBottom: 20 }}>Your account is now protected with two-factor authentication.</div>
            <button onClick={() => navigate('/')} style={{
              padding: '10px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
              background: btnGradient, color: '#fff', fontSize: 13, fontWeight: 700, fontFamily: FONTS.sans,
            }}>Continue to Dashboard</button>
          </div>
        )}
      </div>
    </div>
  );
}
